/**
 * 布局密度 —— 由屏幕档位自动派生（自动适配，无用户开关）
 *
 * ┌── 2026-10-19 改造：密度不再是用户偏好 ──────────────────────────┐
 * │ 工具栏的「密度」按钮（DensitySwitcher）已移除。密度由屏幕档位       │
 * │ （src/constants/screenTier.ts）自动派生，用户不需要也不应该手动选。 │
 * │ 语义：仅 B 档（≥1920）字号上浮一档，其余三档保持同一字号；各档位下    │
 * │ 「字段数/行数/位置」的相对比例由档位层（--tier-*）保证一致。          │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * 档位 → 密度（唯一映射表见下方 TIER_TO_DENSITY）：
 *   B ≥1920        默认 default  16px → 1rem=16px（大屏上浮一档）
 *   A 1280–1919    小   small    14px → 1rem=14px（**主力档，与改造前一致**）
 *   C 768–1279     小   small    14px（与改造前一致）
 *   D <768         小   small    14px（与改造前一致；手机优先可读性，刻意不给 12px）
 *
 * ⚠️ 为什么基准档是 14 而不是 16（**改动前先读这段**）：
 *   改造前 `getAutoDensity()` **恒返回 'small'**，即现网全站根字号本来就是 14px。
 *   若把基准档定义成 16，等于**把主力笔记本档字号整体放大 14%** —— 与本项目
 *   「视觉密度 8、密集优先」的设计基调相反，也不是用户要的「自动适配」。
 *   所以除 B 档**上浮一档**（呼应已拍板的「B 档允许上浮一次」）外，其余三档
 *   一律保持改造前的实际取值 ⇒ 主力设备零观感变化。
 *
 * 密度只做一件事：写 <html> 的 font-size，全站 rem token 随之缩放。
 * 结构与档位的分工（侧栏形态与宽度、行高、列数、触控热区、间距）见
 * constants/screenTier.ts + styles/tier-tokens.css；两者正交：
 * 档位用 px（不随密度缩放），密度用 rem。
 *
 * ── 为什么本模块不自己挂 window resize ──
 * 档位 store 已经有一个全局唯一的 resize 监听（initScreenTier），并且在
 * syncFromViewport() 里用 `next === currentTier` 去重，只在**跨断点**时通知。
 * 因此这里只 subscribeScreenTier()：不重复监听、也不需要额外防抖 ——
 * 拖窗口过程中根字号不会被逐帧改写（根字号是根元素样式，写一次就是一次
 * 全文档样式重算，keep-alive 缓存页越多越贵）。这与 applyDensity 里的
 * 「值没变就不写」是同一道防线。
 */

import { useSyncExternalStore } from 'react';
import {
  getScreenTier,
  subscribeScreenTier,
  type ScreenTier,
} from '@/constants/screenTier';

export type Density = 'large' | 'default' | 'small' | 'mini';

/** 各密度对应的 html font-size（px） */
export const DENSITY_FONT_SIZES: Record<Density, number> = {
  large: 18,
  default: 16,
  small: 14,
  mini: 12,
};

/**
 * 密度中文标签。
 * ⚠️ 保留导出：components/DensitySwitcher 仍引用它（该组件已无任何页面渲染，
 *    按约定保留文件不删，见 2026-10-19 改造说明）。
 */
export const DENSITY_LABELS: Record<Density, string> = {
  large: '大',
  default: '默认',
  small: '小',
  mini: 'mini',
};

/**
 * ⭐ 档位 → 密度：本次改造的唯一事实来源。
 *
 * 取值理由：
 *   A = small(14)   —— **与改造前的实际渲染一致**（改造前自动值恒为 small），
 *                      是「不引入观感变化」的锚点；
 *   B = default(16) —— 大屏上浮一档：既落地已拍板的「B 档允许上浮一次」，
 *                      又取代原先只在看板层生效的 --dash-fs-* ×1.08
 *                      （那个已移除，避免与全局密度双重放大）；
 *   C = small(14)   —— 触控档以「看得清 + 点得准」为先，字号不放大，
 *                      靠 --tier-ctl-min/--tier-row-h 放大热区；
 *   D = small(14)   —— 手机不给 mini(12)：12px 在手持距离下正文会掉到
 *                      9–10px 实感，优先保证可读性。
 *
 * ⚠️ 这张表是密度侧唯一的「语义决策点」：要调整档位字号，只改这里。
 *    （若业务要求「主力笔记本也放大」，把 A 改成 'default' 即可，一行翻转。）
 */
export const TIER_TO_DENSITY: Readonly<Record<ScreenTier, Density>> = {
  B: 'default',
  A: 'small',
  C: 'small',
  D: 'small',
};

/** 纯函数：档位 → 密度（未知档位退到基准档，避免任何 undefined 落到 CSS） */
export function densityForTier(tier: ScreenTier): Density {
  return TIER_TO_DENSITY[tier] ?? TIER_TO_DENSITY.A;
}

/**
 * 旧「密度偏好」localStorage 键。
 *
 * ★ 2026-10-19 起它**不再参与密度计算**：密度由档位决定。
 *   用户手选的档位字号会与视口不匹配 —— 例如在 1280 笔记本上选过「大」，
 *   换到 iPad 上仍是 16px，恰恰破坏「不同屏幕下布局一致」这个目标；
 *   所以旧偏好一律让位给自动值（自动值永远优先）。
 *
 * 保留读取能力的两个用途：
 *   1) 视口宽度测不到时（无 window / 内联 iframe 宽为 0）作为最后兜底；
 *   2) 运行时自检与回滚定位（老用户现场可复现）。
 * 不删除该键：只占几字节，删掉反而让历史现场无法复现；也不写回它
 * （密度已无手动入口，写回只会让下一次改造再踩一次坑）。
 */
const STORAGE_KEY = 'erp-density-preference';

/** 读取旧偏好（兼容用，不参与正常路径）。见 STORAGE_KEY 说明。 */
export function readLegacyDensityPreference(): Density | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'large' || stored === 'default' || stored === 'small' || stored === 'mini') {
      return stored;
    }
  } catch {
    // localStorage 不可用时忽略
  }
  return null;
}

/**
 * 将密度应用到 html font-size。
 *
 * ⚠️ 值没变就**不要写**：`<html>` 的 font-size 是根元素样式，写一次就让整篇
 * 文档的样式失效重算，文档越大（keep-alive 缓存页越多）越贵。
 * 与 constants/screenTier.ts 的 `if (next === currentTier) return` 同一道理。
 */
function applyDensity(density: Density): void {
  if (typeof document === 'undefined') return;
  const next = `${DENSITY_FONT_SIZES[density]}px`;
  const root = document.documentElement;
  if (root.style.fontSize === next) return;
  root.style.fontSize = next;
}

/**
 * 当前应生效的密度：视口 → 档位 → 密度，一步算出。
 *
 * 优先读档位 store（getScreenTier 会先看 <html data-tier>，那是设计走查用
 * setScreenTierOverride 手动锁档时写的，因此手动锁档也能连带锁住字号），
 * 拿不到视口宽度时才退到旧偏好，最后兜底基准档。
 */
export function readDensity(): Density {
  if (typeof window !== 'undefined' && window.innerWidth > 0) {
    return densityForTier(getScreenTier());
  }
  return readLegacyDensityPreference() ?? densityForTier('A');
}

/** 防止重复初始化的标志 */
let densityInitialized = false;

/**
 * initDensity — 全局密度初始化（应用启动时调用）
 *
 * 在 app.tsx 模块顶层**同步**执行，早于任何页面（含登录页）渲染：
 * 登录页不渲染 TabLayout，若靠 hook 设置根字号，首帧会先按浏览器默认 16px
 * 渲染再跳到目标字号（可见闪跳），所以必须在这里一次性写好。
 *
 * 不依赖 initScreenTier() 的先后顺序：这里直接由 window.innerWidth 解析档位，
 * 两个初始化函数谁先谁后结果都一样。之后由档位 store 的订阅接管
 * （跨断点 resize / setScreenTierOverride 都会走到）。
 *
 * 幂等，重复调用无副作用。
 */
export function initDensity(): void {
  if (typeof window === 'undefined' || densityInitialized) return;
  densityInitialized = true;

  applyDensity(readDensity());

  // 订阅档位 store —— 全局唯一的 resize 监听在 initScreenTier 里，本模块不重复监听
  subscribeScreenTier(() => {
    applyDensity(readDensity());
  });
}

/**
 * useDensity — 读取当前密度（只读，无 setter）
 *
 * 与 useScreenTier 同一套路：数据源是档位 store，用 useSyncExternalStore 订阅，
 * 服务端/首帧快照恒定返回基准档 A，避免水合抖动。
 * 密度是派生值，因此这里**不提供任何修改入口** —— 改字号请改 TIER_TO_DENSITY。
 *
 * 注意：根字号的写入只有两个执行点（initDensity 与它的 store 订阅），
 * 本 hook 不写 DOM，避免出现「第二处写根字号」的事实来源。
 */
export function useDensity(): { density: Density; fontPx: number } {
  const tier = useSyncExternalStore(subscribeScreenTier, getScreenTier, () => 'A' as ScreenTier);
  const density = densityForTier(tier);
  return { density, fontPx: DENSITY_FONT_SIZES[density] };
}

export default useDensity;